document.addEventListener('DOMContentLoaded', () => {
  const hero = document.querySelector('.flipside-hero');
  if (!hero) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ============================================================
  // UTILITIES
  // ============================================================
  function lerp(a, b, t) { return a + (b - a) * t; }
  function clamp(val, min, max) { return Math.min(Math.max(val, min), max); }
  function norm(val, min, max) { return clamp((val - min) / (max - min), 0, 1); }

  // ============================================================
  // 1. CAN ENTRANCE ANIMATION (staggered fan-in on load)
  // ============================================================
  const cans = hero.querySelectorAll('.flipside-hero__can--animate');

  if (!reduceMotion) {
    cans.forEach((can) => {
      const delay = parseInt(can.getAttribute('data-delay') || '0', 10);
      setTimeout(() => can.classList.add('is-visible'), 300 + delay);
    });
  } else {
    cans.forEach((can) => can.classList.add('is-visible'));
  }

  hero.querySelectorAll('.flipside-hero__can:not(.flipside-hero__can--animate)').forEach((can) => {
    can.style.opacity = '1';
  });

  // ============================================================
  // 2. HERO CONTENT REVEAL (immediate, on load)
  // ============================================================
  const heroReveal = hero.querySelector('[data-reveal]');
  if (heroReveal) {
    requestAnimationFrame(() => {
      setTimeout(() => heroReveal.classList.add('is-revealed'), 100);
    });
  }

  // ============================================================
  // 3. SCROLL-TRIGGERED REVEALS (welcome + flavours)
  // ============================================================
  const welcomeZoneReveal = hero.querySelector('.flipside-hero__zone--welcome');
  const flavZoneReveal    = hero.querySelector('.flipside-hero__zone--flavours');
  if (welcomeZoneReveal) welcomeZoneReveal.classList.add('pre-reveal');
  if (flavZoneReveal)    flavZoneReveal.classList.add('pre-reveal');

  const scrollRevealEls = hero.querySelectorAll('[data-scroll-reveal]');

  if (scrollRevealEls.length > 0) {
    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('pre-reveal');
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    scrollRevealEls.forEach((el) => revealObserver.observe(el));
  }

  if (reduceMotion) {
    hero.querySelectorAll('.flipside-hero__flavour-can').forEach((can, i) => {
      const baseRots = [-18, 14, -58, -12, 12];
      can.style.transform = `rotate(${baseRots[i] || 0}deg)`;
    });
    return;
  }

  const isMobile = window.matchMedia('(max-width: 749px)').matches;
  const isTouch  = window.matchMedia('(hover: none) and (pointer: coarse)').matches;

  // ============================================================
  // 4. 3D SCROLL ENGINE
  // ============================================================
  if (!isMobile) {
    hero.style.perspective = '1200px';
    hero.style.perspectiveOrigin = '50% 40%';
  }

  const s3 = {
    heroTilt:    { t: 0, c: 0 },
    leftZ:       { t: 0, c: 0 },
    rightZ:      { t: 0, c: 0 },
    welcomeRot:  { t: 0, c: 0 },
    flavourRot:  { t: 0, c: 0 },
    flavourZ:    { t: 0, c: 0 },
    marqueeSkew: { t: 0, c: 0 },
  };

  const heroZone    = hero.querySelector('.flipside-hero__zone--hero');
  const welcomeZone = hero.querySelector('.flipside-hero__zone--welcome');
  const flavZone    = hero.querySelector('.flipside-hero__zone--flavours');
  const marqueeZone = hero.querySelector('.flipside-hero__zone--marquee');
  const leftCans    = hero.querySelector('.flipside-hero__hero-cans--left');
  const rightCans   = hero.querySelector('.flipside-hero__hero-cans--right');

  [heroZone, welcomeZone, flavZone].forEach((el) => {
    if (el) el.style.transformStyle = 'preserve-3d';
  });

  const flavourCanConfigs = [
    { sel: '.flipside-hero__flavour-can--1', baseRot: -18, spin:  55, speed: 1.1 },
    { sel: '.flipside-hero__flavour-can--2', baseRot:  14, spin: -70, speed: 0.7 },
    { sel: '.flipside-hero__flavour-can--3', baseRot: -58, spin:  80, speed: 1.4 },
    { sel: '.flipside-hero__flavour-can--4', baseRot: -12, spin: -50, speed: 0.9 },
    { sel: '.flipside-hero__flavour-can--5', baseRot:  12, spin:  60, speed: 1.2 },
  ];

  const flavourCanData = flavourCanConfigs.map(({ sel, baseRot, spin, speed }) => {
    const el = hero.querySelector(sel);
    return { el, baseRot, spin, speed, curRot: baseRot, curY: 0, tRot: baseRot, tY: 0 };
  }).filter(d => d.el !== null);

  function updateScrollTargets() {
    const heroRect = hero.getBoundingClientRect();
    const vh = window.innerHeight;

    if (heroZone && !isMobile) {
      const p = norm(heroRect.top, vh * 0.5, -heroRect.height * 0.5);
      s3.heroTilt.t = p * -8;
    }

    if (heroZone && !isMobile) {
      const p = norm(heroRect.top, vh, -heroRect.height);
      s3.leftZ.t  = p * 90;
      s3.rightZ.t = p * 65;
    }

    if (welcomeZone && !isMobile) {
      const r = welcomeZone.getBoundingClientRect();
      const p = norm(r.top, vh * 0.95, vh * 0.1);
      s3.welcomeRot.t = (1 - p) * 38;
    }

    if (flavZone && !isMobile) {
      const r = flavZone.getBoundingClientRect();
      const p = norm(r.top, vh * 0.9, vh * 0.05);
      s3.flavourRot.t = (1 - p) * -24;
      s3.flavourZ.t   = p * 55;
    }

    if (flavZone) {
      const r        = flavZone.getBoundingClientRect();
      const progress = norm(r.top, vh * 1.1, -r.height * 0.6);
      const intensity = isMobile ? 0.35 : 1;
      flavourCanData.forEach(d => {
        d.tRot = d.baseRot + progress * d.spin * intensity;
        d.tY   = -progress * d.speed * 80 * intensity;
      });
    }

    if (marqueeZone && !isMobile) {
      const r   = marqueeZone.getBoundingClientRect();
      const mid = r.top + r.height / 2;
      const p   = norm(mid, vh, 0);
      s3.marqueeSkew.t = (p - 0.5) * 12;
    }
  }

  // ============================================================
  // 5. MOUSE PARALLAX STATE
  // ============================================================
  const bg = hero.querySelector('.flipside-hero__bg');
  let mouseX = 0, mouseY = 0, curMX = 0, curMY = 0;
  let isInHero = false;

  if (!isTouch) {
    hero.addEventListener('mouseenter', () => { isInHero = true; });
    hero.addEventListener('mousemove', (e) => {
      const r = hero.getBoundingClientRect();
      mouseX = (e.clientX - r.left  - r.width  / 2) / r.width;
      mouseY = (e.clientY - r.top   - r.height / 2) / r.height;
    });
    hero.addEventListener('mouseleave', () => {
      isInHero = false;
      mouseX = 0;
      mouseY = 0;
    });
  }

  // ============================================================
  // 6. UNIFIED RAF LOOP
  // ============================================================
  function tick() {
    requestAnimationFrame(tick);

    for (const key in s3) {
      s3[key].c = lerp(s3[key].c, s3[key].t, 0.07);
    }

    curMX = lerp(curMX, mouseX, 0.08);
    curMY = lerp(curMY, mouseY, 0.08);

    const mx = isInHero ? curMX : curMX * 0.3;
    const my = isInHero ? curMY : curMY * 0.3;

    // Only update hero zone transform if no button is being hovered
    if (heroZone && !isMobile && !heroZone.dataset.frozen) {
      const rotX = s3.heroTilt.c + (mx * -3);
      const rotY = mx * 6;
      heroZone.style.transform = `rotateX(${rotX}deg) rotateY(${rotY}deg)`;
    }

    if (leftCans && !isMobile) {
      leftCans.style.transform =
        `translateZ(${s3.leftZ.c}px) translate(${mx * -20}px, ${my * -10}px)`;
    }
    if (rightCans && !isMobile) {
      rightCans.style.transform =
        `translateZ(${s3.rightZ.c}px) translate(${mx * 20}px, ${my * -10}px)`;
    }

    if (welcomeZone) {
      if (!isMobile) {
        welcomeZone.style.transform = `perspective(900px) rotateX(${s3.welcomeRot.c}deg)`;
        welcomeZone.style.transformOrigin = 'center bottom';
        const wOpacity = welcomeZone.classList.contains('is-revealed')
          ? clamp(norm(s3.welcomeRot.c, 38, 6), 0.15, 1)
          : 0;
        welcomeZone.style.opacity = String(wOpacity);
      } else {
        welcomeZone.style.transform = 'none';
        welcomeZone.style.opacity = welcomeZone.classList.contains('is-revealed') ? '1' : '0';
      }
    }

    if (flavZone) {
      if (!isMobile) {
        flavZone.style.transform = `perspective(1100px) rotateY(${s3.flavourRot.c}deg)`;
        flavZone.style.transformOrigin = 'center center';
        const fOpacity = flavZone.classList.contains('is-revealed')
          ? clamp(norm(s3.flavourRot.c, -24, -4), 0.15, 1)
          : 0;
        flavZone.style.opacity = String(fOpacity);
      } else {
        flavZone.style.transform = 'none';
        flavZone.style.opacity = flavZone.classList.contains('is-revealed') ? '1' : '0';
      }
    }

    flavourCanData.forEach(d => {
      d.curRot = lerp(d.curRot, d.tRot, 0.06);
      d.curY   = lerp(d.curY,   d.tY,   0.06);
      d.el.style.transform = `rotate(${d.curRot}deg) translateY(${d.curY}px)`;
    });

    if (marqueeZone && !isMobile) {
      marqueeZone.style.transform = `skewX(${s3.marqueeSkew.c}deg)`;
    }

    const centerContent = hero.querySelector('[data-parallax-group="center"]');
    if (centerContent && !centerContent.dataset.frozen) {
      centerContent.style.transform = `translate(${mx * 5}px, ${my * 3}px)`;
    }

    if (bg) {
      bg.style.transform = `scale(1.08) translate(${mx * -18}px, ${my * -12}px)`;
    }
  }

  window.addEventListener('scroll', updateScrollTargets, { passive: true });
  updateScrollTargets();
  tick();

  // ============================================================
  // 7. BTN-FLIPSIDE — freeze hero zone transform on button hover
  //    so CSS press-in effect isn't overridden by the RAF loop
  // ============================================================
  hero.querySelectorAll('.btn-flipside').forEach(btn => {
    btn.addEventListener('mouseenter', () => {
      if (heroZone) heroZone.dataset.frozen = '1';
      const centerContent = hero.querySelector('[data-parallax-group="center"]');
      if (centerContent) centerContent.dataset.frozen = '1';
    });
    btn.addEventListener('mouseleave', () => {
      if (heroZone) delete heroZone.dataset.frozen;
      const centerContent = hero.querySelector('[data-parallax-group="center"]');
      if (centerContent) delete centerContent.dataset.frozen;
    });
  });

  // ============================================================
  // 8. MARQUEE — pause on hover
  // ============================================================
  const marqueeTrack = hero.querySelector('.flipside-marquee__track');
  if (marqueeTrack) {
    marqueeTrack.addEventListener('mouseenter', () => {
      marqueeTrack.style.animationPlayState = 'paused';
    });
    marqueeTrack.addEventListener('mouseleave', () => {
      marqueeTrack.style.animationPlayState = 'running';
    });
  }

  // ============================================================
  // 9. FLAVOUR LABEL — tilt on hover
  // ============================================================
  const flavourLabels = hero.querySelectorAll('.flipside-hero__flavour-label');
  flavourLabels.forEach((label) => {
    label.addEventListener('mouseenter', (e) => {
      const r = label.getBoundingClientRect();
      const tilt = e.clientX < r.left + r.width / 2 ? -4 : 4;
      label.style.setProperty('--label-tilt', `${tilt}deg`);
    });
    label.addEventListener('mouseleave', () => {
      label.style.removeProperty('--label-tilt');
    });
  });
});

